<template>
    <div v-if="usdcMethod">
        <div v-if="provisioning" class="text-center py-5">
            <b-spinner variant="primary"></b-spinner>
            <div class="mt-3 text-secondary">
                Creating your wallet address…
            </div>
            <div class="small text-muted mt-1">
                This usually takes just a few seconds
            </div>
        </div>

        <b-alert v-else-if="error" show variant="danger" class="mt-3">
            <div class="mb-2">
                <strong>Something went wrong.</strong>
                We couldn't create your wallet right now. Please try again in a few moments.
            </div>
            <b-btn @click="provisionWallet" variant="outline-danger" size="sm">
                Try Again
            </b-btn>
        </b-alert>

        <template v-else-if="usdcMethod.address">
            <div class="usdc-hero">
                <h4 class="usdc-hero-title">
                    Your Permanent USDC Wallet Address
                </h4>
                <p class="usdc-hero-sub">
                    Save it once. Send USDC anytime — we turn it into credits automatically,
                    plus a <strong>10% bonus</strong> every single time.
                </p>
            </div>

            <div class="address-card">
                <div class="address-card-header">
                    <div class="address-card-label">
                        <font-awesome-icon icon="fa-solid fa-wallet" class="mr-2" />
                        Wallet Address
                    </div>
                    <span class="permanent-tag">
                        <font-awesome-icon icon="fa-solid fa-infinity" class="mr-1" />
                        PERMANENT
                    </span>
                </div>
                <div class="address-display">
                    {{ usdcMethod.address }}
                </div>
                <b-btn
                    block
                    size="lg"
                    variant="primary"
                    @click="copyAddress"
                    class="copy-btn mt-3"
                >
                    <font-awesome-icon
                        :icon="copied ? 'fa-light fa-check' : 'fa-light fa-copy'"
                        class="mr-2"
                    />
                    {{ copied ? 'Copied to clipboard!' : 'Copy Address' }}
                </b-btn>
                <div class="address-tip">
                    <font-awesome-icon icon="fa-solid fa-lightbulb" class="mr-2" />
                    Add this to your Coinbase address book once — then it's one tap to send next time.
                </div>
            </div>

            <h5 class="steps-heading">How it works</h5>
            <div class="how-row">
                <div class="how-card">
                    <div class="how-icon how-icon-1">
                        <font-awesome-icon icon="fa-solid fa-store" />
                    </div>
                    <div class="how-card-body">
                        <div class="how-step">Step 1</div>
                        <h6 class="how-title">Get USDC</h6>
                        <p class="how-text">
                            Buy USDC from Coinbase — the most popular place to get it.
                            Takes a few minutes, signup is free.
                        </p>
                        <a
                            href="https://www.coinbase.com/signup"
                            @click.prevent="openBrowser('https://www.coinbase.com/signup')"
                            class="how-link"
                        >
                            Sign up free at Coinbase
                            <font-awesome-icon icon="fa-light fa-arrow-up-right-from-square" class="ml-1" />
                        </a>
                    </div>
                </div>
                <div class="how-card">
                    <div class="how-icon how-icon-2">
                        <font-awesome-icon icon="fa-solid fa-paper-plane" />
                    </div>
                    <div class="how-card-body">
                        <div class="how-step">Step 2</div>
                        <h6 class="how-title">Send to Your Address</h6>
                        <p class="how-text">
                            Paste your address into Coinbase. Pick
                            <strong>USDC on Ethereum</strong>.
                            Send <strong>$25 or more</strong> — no upper limit,
                            any amount becomes credits.
                        </p>
                    </div>
                </div>
                <div class="how-card">
                    <div class="how-icon how-icon-3">
                        <font-awesome-icon icon="fa-solid fa-sparkles" />
                    </div>
                    <div class="how-card-body">
                        <div class="how-step">Step 3</div>
                        <h6 class="how-title">Credits Arrive</h6>
                        <p class="how-text">
                            Usually within <strong>15 minutes</strong>.
                            Plus your <strong>10% bonus</strong>, on us — every time.
                        </p>
                    </div>
                </div>
            </div>

            <div class="trust-row">
                <div class="trust-item">
                    <font-awesome-icon icon="fa-solid fa-shield-check" />
                    <div class="trust-label">Safe</div>
                </div>
                <div class="trust-item">
                    <font-awesome-icon icon="fa-solid fa-infinity" />
                    <div class="trust-label">Permanent</div>
                </div>
                <div class="trust-item trust-item-bonus">
                    <font-awesome-icon icon="fa-solid fa-gift" />
                    <div class="trust-label">+10% Bonus</div>
                </div>
            </div>

            <div class="help-section">
                <b-btn
                    variant="link"
                    block
                    class="help-toggle"
                    @click="showRates = !showRates"
                >
                    <font-awesome-icon
                        :icon="showRates ? 'fa-solid fa-circle-minus' : 'fa-solid fa-chart-line-up'"
                        class="mr-2"
                    />
                    {{ showRates ? 'Hide bulk discount rates' : 'Send more, save more — see bulk rates' }}
                </b-btn>
                <div v-if="showRates" class="rates-content">
                    <div class="rates-intro">
                        Every refill includes your <strong>10% crypto bonus</strong>.
                        Larger sends earn an even better per-credit rate.
                    </div>
                    <div class="rate-list">
                        <div
                            v-for="(price, index) in creditRefillPricesCrypto"
                            :key="price.id"
                            class="rate-card"
                            :class="{ 'rate-card-best': index >= creditRefillPricesCrypto.length - 2 }"
                        >
                            <div class="rate-card-amount">
                                <div class="rate-amount-label">Send</div>
                                <div class="rate-amount">${{ price.amount }}</div>
                            </div>
                            <div class="rate-card-credits">
                                <div class="rate-credits-row">
                                    <span class="rate-credits-base">
                                        {{ formatNumber(price.credits) }} credits
                                    </span>
                                    <span class="rate-bonus-pill">
                                        +{{ formatNumber(price.bonusCredits) }} bonus
                                    </span>
                                </div>
                                <div class="rate-credits-total">
                                    {{ formatNumber(price.totalCredits) }} total
                                </div>
                            </div>
                            <div class="rate-card-rate">
                                <span
                                    v-if="index >= creditRefillPricesCrypto.length - 2"
                                    class="rate-best-tag"
                                >
                                    BEST RATE
                                </span>
                                <div class="rate-per-credit">
                                    {{ price.label }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <b-btn
                    variant="link"
                    block
                    class="help-toggle mt-2"
                    @click="showHelp = !showHelp"
                >
                    <font-awesome-icon
                        :icon="showHelp ? 'fa-solid fa-circle-minus' : 'fa-solid fa-circle-question'"
                        class="mr-2"
                    />
                    {{ showHelp ? 'Hide help' : 'New to crypto? Tap here for help' }}
                </b-btn>
                <div v-if="showHelp" class="help-content">
                    <div class="help-item">
                        <h6>
                            <font-awesome-icon icon="fa-solid fa-circle-dollar" class="mr-2 text-primary" />
                            What is USDC?
                        </h6>
                        <p>
                            USDC is a digital dollar. <strong>1 USDC always equals $1</strong>.
                            It doesn't swing in price like Bitcoin. It's regulated and used by millions of people every day.
                        </p>
                    </div>
                    <div class="help-item">
                        <h6>
                            <font-awesome-icon icon="fa-solid fa-shield-halved" class="mr-2 text-primary" />
                            Is this safe?
                        </h6>
                        <p>
                            Yes. Your wallet address is yours and only yours. You can reuse it
                            forever, share it with no one but us, and it will never expire.
                            USDC is one of the most trusted digital currencies in the world.
                        </p>
                    </div>
                    <div class="help-item">
                        <h6>
                            <font-awesome-icon icon="fa-solid fa-network-wired" class="mr-2 text-primary" />
                            What does "Ethereum" mean?
                        </h6>
                        <p>
                            It's just a network — like how Visa and Mastercard are networks for credit cards.
                            When you send from Coinbase, pick <strong>"USDC on Ethereum"</strong> from the dropdown.
                            Don't pick anything else.
                        </p>
                    </div>
                    <div class="help-item">
                        <h6>
                            <font-awesome-icon icon="fa-solid fa-magnifying-glass-dollar" class="mr-2 text-primary" />
                            Nervous about your first time?
                        </h6>
                        <p>
                            Totally normal. Send <strong>$25</strong> the first time to make sure everything works.
                            Once you see the credits land on your account, you'll know your setup is good
                            and you can send more confidently next time.
                        </p>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import { mapState } from "vuex";

export default {
    data() {
        return {
            copied: false,
            provisioning: false,
            error: false,
            showHelp: false,
            showRates: false,
        };
    },
    mounted() {
        if (this.usdcMethod && !this.usdcMethod.address) {
            this.provisionWallet();
        }
    },
    computed: {
        ...mapState("paymentMethods", {
            paymentMethods: "methods",
        }),
        ...mapState("options", ["creditRefillPricesCrypto"]),
        usdcMethod() {
            return this.paymentMethods?.find((pm) => pm.type === "usdc");
        },
    },
    methods: {
        formatNumber(value) {
            return Number(value).toLocaleString();
        },
        provisionWallet() {
            this.error = false;
            this.provisioning = true;

            this.api
                .post("/nowpayments/createWallet", { type: "usdc" })
                .then(() => {
                    return this.$store.dispatch("paymentMethods/refresh");
                })
                .then(() => {
                    this.provisioning = false;
                })
                .catch(() => {
                    this.provisioning = false;
                    this.error = true;
                });
        },
        copyAddress() {
            navigator.clipboard.writeText(this.usdcMethod.address);
            this.copied = true;

            setTimeout(() => {
                this.copied = false;
            }, 2200);
        },
        openBrowser(url) {
            this.$store.dispatch("system/openBrowser", url);
        },
    },
};
</script>

<style scoped>
.usdc-hero {
    text-align: center;
    padding: 1.5rem 0 1rem;
}

.usdc-hero-title {
    font-weight: 700;
    letter-spacing: -0.01em;
    margin-bottom: 0.5rem;
}

.usdc-hero-sub {
    color: var(--secondary, #6c757d);
    font-size: 1rem;
    line-height: 1.5;
    max-width: 480px;
    margin: 0 auto;
}

.address-card {
    background: linear-gradient(135deg, rgba(232, 69, 124, 0.06), rgba(244, 162, 97, 0.06));
    border: 1px solid rgba(232, 69, 124, 0.18);
    border-radius: 16px;
    padding: 1.25rem;
    margin: 1.25rem 0;
    box-shadow: 0 8px 30px rgba(232, 69, 124, 0.08);
}

.address-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.75rem;
}

.address-card-label {
    font-size: 0.85rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--secondary, #6c757d);
}

.permanent-tag {
    background: linear-gradient(90deg, #e8457c, #f4a261);
    color: #fff;
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    padding: 0.25rem 0.55rem;
    border-radius: 999px;
}

.address-display {
    font-family: SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace;
    font-size: 0.95rem;
    word-break: break-all;
    background: rgba(255, 255, 255, 0.65);
    border: 1px dashed rgba(0, 0, 0, 0.12);
    border-radius: 10px;
    padding: 0.9rem 1rem;
    line-height: 1.5;
}

body.dark .address-display {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.12);
    color: #eee;
}

.copy-btn {
    border-radius: 12px;
    font-weight: 600;
    letter-spacing: 0.01em;
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
}

.address-tip {
    margin-top: 0.75rem;
    font-size: 0.85rem;
    color: var(--secondary, #6c757d);
    text-align: center;
}

.steps-heading {
    margin-top: 2rem;
    margin-bottom: 1rem;
    font-weight: 600;
    text-align: center;
    letter-spacing: -0.01em;
}

.how-row {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    margin-bottom: 1.5rem;
}

.how-card {
    display: flex;
    align-items: flex-start;
    gap: 0.9rem;
    background: var(--white, #fff);
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 14px;
    padding: 0.9rem 1rem;
    text-align: left;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

body.dark .how-card {
    background: #2a2a2a;
    border-color: rgba(255, 255, 255, 0.08);
}

.how-card:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.06);
}

.how-card-body {
    flex: 1;
    min-width: 0;
}

.how-icon {
    flex-shrink: 0;
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
    color: #fff;
}

.how-icon-1 {
    background: linear-gradient(135deg, #6610f2, #007bff);
}

.how-icon-2 {
    background: linear-gradient(135deg, #e8457c, #f4a261);
}

.how-icon-3 {
    background: linear-gradient(135deg, #20c997, #007bff);
}

.how-step {
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--secondary, #6c757d);
    margin-bottom: 0.25rem;
}

.how-title {
    font-weight: 700;
    margin-bottom: 0.5rem;
}

.how-text {
    font-size: 0.9rem;
    line-height: 1.5;
    color: var(--secondary, #6c757d);
    margin-bottom: 0.75rem;
}

.how-link {
    display: inline-flex;
    align-items: center;
    font-weight: 600;
    font-size: 0.9rem;
    text-decoration: none;
}

.how-link:hover {
    text-decoration: underline;
}

.trust-row {
    display: flex;
    justify-content: center;
    align-items: stretch;
    gap: 0.75rem;
    margin: 1.5rem 0;
}

.trust-item {
    flex: 1;
    text-align: center;
    padding: 0.85rem 0.5rem;
    background: rgba(0, 123, 255, 0.05);
    border-radius: 12px;
    color: var(--primary, #007bff);
    font-weight: 600;
    font-size: 0.85rem;
}

body.dark .trust-item {
    background: rgba(0, 123, 255, 0.12);
}

.trust-item svg {
    font-size: 1.1rem;
    margin-bottom: 0.3rem;
    display: block;
    margin-left: auto;
    margin-right: auto;
}

.trust-item-bonus {
    background: linear-gradient(135deg, rgba(232, 69, 124, 0.12), rgba(244, 162, 97, 0.12));
    color: #e8457c;
}

body.dark .trust-item-bonus {
    background: linear-gradient(135deg, rgba(232, 69, 124, 0.22), rgba(244, 162, 97, 0.22));
    color: #ff6b9d;
}

.trust-label {
    margin-top: 0.1rem;
}

.help-section {
    margin-top: 1.5rem;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
    padding-top: 1rem;
}

body.dark .help-section {
    border-top-color: rgba(255, 255, 255, 0.08);
}

.help-toggle {
    font-weight: 600;
    text-decoration: none !important;
}

.help-content {
    margin-top: 0.5rem;
    padding: 0.5rem 0.5rem 0;
}

.rates-content {
    margin-top: 0.5rem;
    padding: 0.5rem 0.25rem 0;
}

.rates-intro {
    font-size: 0.9rem;
    line-height: 1.5;
    color: var(--secondary, #6c757d);
    text-align: center;
    margin-bottom: 0.85rem;
}

.rate-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.rate-card {
    display: flex;
    align-items: center;
    gap: 0.85rem;
    background: var(--white, #fff);
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 12px;
    padding: 0.75rem 0.9rem;
    transition: transform 0.15s ease, box-shadow 0.15s ease;
}

body.dark .rate-card {
    background: #2a2a2a;
    border-color: rgba(255, 255, 255, 0.08);
}

.rate-card:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06);
}

.rate-card-best {
    background: linear-gradient(135deg, rgba(232, 69, 124, 0.08), rgba(244, 162, 97, 0.08));
    border-color: rgba(232, 69, 124, 0.3);
    box-shadow: 0 4px 16px rgba(232, 69, 124, 0.1);
}

body.dark .rate-card-best {
    background: linear-gradient(135deg, rgba(232, 69, 124, 0.16), rgba(244, 162, 97, 0.16));
    border-color: rgba(232, 69, 124, 0.4);
}

.rate-card-amount {
    flex-shrink: 0;
    width: 70px;
    text-align: left;
}

.rate-amount-label {
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--secondary, #6c757d);
    line-height: 1;
}

.rate-amount {
    font-size: 1.2rem;
    font-weight: 700;
    margin-top: 0.15rem;
    line-height: 1;
}

.rate-card-credits {
    flex: 1;
    min-width: 0;
}

.rate-credits-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.35rem;
    font-size: 0.88rem;
}

.rate-credits-base {
    font-weight: 600;
}

.rate-bonus-pill {
    background: linear-gradient(90deg, #e8457c, #f4a261);
    color: #fff;
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    padding: 0.15rem 0.5rem;
    border-radius: 999px;
}

.rate-credits-total {
    font-size: 0.78rem;
    color: var(--secondary, #6c757d);
    margin-top: 0.2rem;
}

.rate-card-rate {
    flex-shrink: 0;
    text-align: right;
    min-width: 90px;
}

.rate-best-tag {
    display: inline-block;
    background: #e8457c;
    color: #fff;
    font-size: 0.6rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    padding: 0.15rem 0.45rem;
    border-radius: 999px;
    margin-bottom: 0.25rem;
}

.rate-per-credit {
    font-size: 0.82rem;
    font-weight: 600;
    color: var(--primary, #007bff);
}

.rate-card-best .rate-per-credit {
    color: #e8457c;
}

body.dark .rate-card-best .rate-per-credit {
    color: #ff6b9d;
}

.help-item {
    margin-bottom: 1rem;
}

.help-item h6 {
    font-weight: 700;
    margin-bottom: 0.4rem;
}

.help-item p {
    font-size: 0.9rem;
    line-height: 1.55;
    color: var(--secondary, #6c757d);
    margin-bottom: 0;
}
</style>
